import { Divider, Form, message, Modal, Select, Typography } from 'antd'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { entryFromLibraTicket } from 'features/libra/ticket/libraTicketsApi'
import { useLibraWorkflowSteps } from 'features/libra/workflow/workflowStepsApi'
import { generateEntrySlug, useRolodexEntries } from 'features/rolodex'
import debounce from 'lodash/debounce'
import first from 'lodash/first'
import { FC, useCallback, useMemo, useRef, useState } from 'react'
import { GeckoUnit, RolodexEntryType } from 'types'
import { v4 as uuid } from 'uuid'

const { Text } = Typography

interface CreateTicketsForGroupModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  organizationId: string
  groupId: string
  groupTitle: string
}

interface FormValues {
  workflowId: string
  unitIds: string[]
}

export const CreateTicketsForGroupModal: FC<
  CreateTicketsForGroupModalProps
> = ({ isOpen, onClose, onSuccess, organizationId, groupId, groupTitle }) => {
  const [form] = Form.useForm<FormValues>()
  const [loading, setLoading] = useState(false)
  const [searchText, setSearchText] = useState('')
  const { rolodexPut } = useRolodexEdit()
  const [messageApi, contextHolder] = message.useMessage()

  // Fetch workflows for this organization
  const { libraWorkflows, isLoading: areWorkflowsLoading } =
    useLibraWorkflowSteps(
      useMemo(
        () => ({
          tags: { organization: organizationId },
        }),
        [organizationId]
      )
    )

  const unitsQuery = useUnitQuery({ searchText, organizationId })

  // Fetch units based on search query
  const { units: latestUnits, isLoading: areUnitsLoading } =
    useRolodexEntries(unitsQuery)

  const unitLookupRef = useRef<Map<string, GeckoUnit>>(new Map())
  const unitMap = useMemo(() => {
    const map = unitLookupRef.current
    latestUnits.forEach((unit) => map.set(unit.id, unit))

    return map
  }, [latestUnits])

  const { workflowOptions, unitOptions } = useMemo(() => {
    return {
      workflowOptions: libraWorkflows.map((workflow) => ({
        label: workflow.name,
        value: workflow.id,
      })),
      unitOptions: latestUnits.map((unit) => ({
        label: unit.data.name || unit.id,
        value: unit.id,
      })),
    }
  }, [libraWorkflows, latestUnits])

  // Debounced search handler
  const debouncedSearch = useRef(
    debounce((value: string) => {
      setSearchText(value)
    }, 300)
  ).current

  const handleSearch = useCallback(
    (value: string) => {
      debouncedSearch(value)
    },
    [debouncedSearch]
  )

  const resetModal = useCallback(() => {
    form.resetFields()
    setSearchText('')
    unitLookupRef.current.clear()
  }, [form])

  const handleSubmit = async (values: FormValues) => {
    setLoading(true)
    try {
      const selectedWorkflow = libraWorkflows.find(
        (w) => w.id === values.workflowId
      )

      if (!selectedWorkflow) {
        messageApi.error('Selected workflow not found')
        return
      }

      const defaultStepId = first(selectedWorkflow.steps)?.id ?? ''

      // Create a ticket for each selected unit
      const ticketsToCreate = values.unitIds.map((unitId) => {
        const unit = unitMap.get(unitId)
        const ticketId = generateEntrySlug('ticket', uuid().slice(0, 6))
        const workflowName = selectedWorkflow.name
        const unitName = unit?.data.name || unitId

        const lifecycleDefault = selectedWorkflow.lifecycleConfig
          ? (selectedWorkflow.lifecycleConfig.stages.find((s) => s.default)
              ?.id ??
            selectedWorkflow.lifecycleConfig.stages[0]?.id ??
            '')
          : ''

        return entryFromLibraTicket({
          id: ticketId,
          title: `${workflowName} - ${unitName}`,
          fieldValues: {},
          stepId: defaultStepId,
          workflowComplete: false,
          version: 1,
          assessmentId: uuid(),
          completedBy: null,
          createdBy: null,
          lastUpdatedBy: null,
          ticketStatus: lifecycleDefault,
          terminalTransitionBy: null,
          terminalTransitionAt: null,
          contributors: [],
          inputProvenance: {},
          workflowId: values.workflowId,
          tags: {
            organization: organizationId,
            site: unit?.tags.site || '',
            unit: unitId,
            [RolodexEntryType.LIBRA_WORKFLOW]: values.workflowId,
            [RolodexEntryType.LIBRA_TICKET_GROUP]: groupId,
          },
        })
      })

      await rolodexPut(ticketsToCreate)
      messageApi.success(
        `${ticketsToCreate.length} ticket${ticketsToCreate.length > 1 ? 's' : ''} created successfully!`
      )

      resetModal()
      onSuccess()
      onClose()
    } catch (error) {
      console.error('Error creating tickets:', error)
      messageApi.error('Failed to create tickets. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    resetModal()
    onClose()
  }

  return (
    <>
      {contextHolder}
      <Modal
        title={`Create Tickets for "${groupTitle}"`}
        open={isOpen}
        onOk={form.submit}
        onCancel={handleCancel}
        confirmLoading={loading}
        okText='Create Tickets'
        cancelText='Cancel'
        width={700}>
        <Form form={form} layout='vertical' onFinish={handleSubmit}>
          <Form.Item
            name='workflowId'
            label='Workflow Template'
            rules={[{ required: true, message: 'Please select a workflow' }]}>
            <Select
              showSearch
              placeholder='Select workflow'
              loading={areWorkflowsLoading}
              options={workflowOptions}
              optionFilterProp='label'
            />
          </Form.Item>

          <Divider />

          <Form.Item
            name='unitIds'
            label={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                }}>
                <span>Units/Assets</span>
                <Text
                  type='secondary'
                  style={{ fontSize: '12px', fontWeight: 'normal' }}>
                  {searchText.trim().length < 2
                    ? `Showing first ${latestUnits.length} units. Type to search for specific units.`
                    : `Found ${latestUnits.length} unit${latestUnits.length !== 1 ? 's' : ''} matching "${searchText}"`}
                </Text>
              </div>
            }
            rules={[
              { required: true, message: 'Please select at least one unit' },
            ]}>
            <Select
              mode='multiple'
              showSearch
              onSearch={handleSearch}
              placeholder='Type to search units...'
              loading={areUnitsLoading}
              filterOption={false}
              options={unitOptions}
              notFoundContent={
                areUnitsLoading
                  ? 'Searching...'
                  : searchText.trim().length < 2
                    ? 'Type at least 2 characters to search'
                    : 'No units found'
              }
              maxTagCount='responsive'
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

const useUnitQuery = ({
  searchText,
  organizationId,
}: {
  searchText: string
  organizationId: string
}) => {
  return useMemo(() => {
    const baseQuery = {
      type: RolodexEntryType.UNIT,
      tags: { organization: organizationId },
    }

    // If no search text, show first 30 units sorted by name
    if (!searchText || searchText.trim().length < 2) {
      return {
        ...baseQuery,
        limit: 30,
        order_by: [
          {
            key: 'name',
            descending: false,
          },
        ],
      }
    }

    // Search by unit name
    return {
      ...baseQuery,
      filters: [
        {
          key: 'name',
          value: searchText.trim(),
        },
      ],
      limit: 100,
    }
  }, [searchText, organizationId])
}
